import { useRouter } from 'next/router'

import clsx from 'clsx'
import useSWR from 'swr'

import { EventFeedDocument, EventOrder } from '@/generated/contentful'

import { client } from '@/lib/contentful/client'
import { normalizeImage } from '@/lib/contentful/normalizers/image'

import { Card } from '@/components/Card'

import { serializeCacheKey } from '../../../../../lib/contentful/utils'

type Props = {
	className?: string
	type?: 'Regular' | 'Featured'
	count?: number
}

export const limit = 20

export function EventFeed({ className, type, count = limit }: Props) {
	const { locale, isPreview } = useRouter()
	const contentful = client({ preview: isPreview })
	const isFeatured = type === 'Featured'

	const { data: regular } = useSWR(
		isFeatured ? null : serializeCacheKey('mainEventsFeed', { locale, limit, featured: false }),
		() =>
			contentful.request(EventFeedDocument, {
				where: { featured: false, status: true },
				order: [EventOrder.RecurringEventDesc, EventOrder.StartDateAsc],
				limit: count,
				locale,
				preview: isPreview
			})
	)

	const { data: featured } = useSWR(
		!isFeatured
			? null
			: serializeCacheKey('featuredEventsFeed', { locale, limit: 5, featured: true }),
		() =>
			contentful.request(EventFeedDocument, {
				where: { featured: true, status: true },
				order: [EventOrder.StartDateAsc],
				limit: 5,
				locale,
				preview: isPreview
			})
	)
	const feed = type === 'Featured' ? featured : regular

	if (!feed) {
		return null
	}

	return (
		<div
			className={clsx(
				className,
				'grid gap-8',
				isFeatured ? 'md:grid-cols-1' : 'sm:grid-cols-2 lg:grid-cols-3'
			)}
		>
			{feed.eventCollection?.items?.map((item, idx) => {
				const title = item?.title
				const externalUrl = item?.url?.url
				const image = normalizeImage(item?.image)

				const getStartDate = () => {
					if (!item?.startDate) return undefined
					return new Intl.DateTimeFormat(locale, {
						year: item?.endDate ? undefined : 'numeric',
						month: 'long',
						day: 'numeric'
					}).format(new Date(item?.startDate))
				}

				const getEndDate = () => {
					if (!item?.endDate) return undefined
					return new Intl.DateTimeFormat(locale, {
						year: 'numeric',
						month: 'long',
						day: 'numeric'
					}).format(new Date(item?.endDate))
				}

				const getDate = () => {
					if (item?.recurringEvent) {
						return 'Various Dates'
					}
					if (item?.startDate) {
						return `${getStartDate()}${item?.endDate ? ` - ${getEndDate()}` : ''}`
					}
					return undefined
				}

				if (!title || !externalUrl) return null

				return (
					<Card
						key={externalUrl}
						heading={getDate()}
						title={title}
						text={item?.summary ?? undefined}
						imageSrc={image}
						imageAlt={image?.alt ?? title}
						linkText="Learn More"
						linkURL={{ href: externalUrl, target: '_blank' }}
						bgColor="#000000"
						isFeatured={isFeatured}
						isReversed={isFeatured && idx % 2 === 0}
						imageRatio={isFeatured ? 'video' : 'none'}
						data-testid={isFeatured ? 'event-feed-card-featured' : 'event-feed-card'}
					/>
				)
			})}
		</div>
	)
}
